// Regras do editor de minicursos: padrões, comando, rótulos, avisos de qualidade
// e impacto das mudanças de sequência nos alunos em andamento.
// Tudo aqui espelha o comportamento do bot (backend/server.js).

export const PADRAO_INTERVALO = 1440; // minutos (bot: `|| 1440`)
export const PADRAO_APROVACAO = 70; // % (bot: `|| 70`)
export const PADRAO_LEMBRETE = 48; // horas; 0 = desligado

export const LIMITES = {
  intervalo: { min: 1, max: 43200 },
  aprovacao: { min: 1, max: 100 },
  lembrete: { min: 0, max: 720 },
  comando: 40,
  nome: 120,
};

export const ATALHOS_INTERVALO = [
  { minutos: 1, rotulo: '1 min (teste)' },
  { minutos: 60, rotulo: '1 hora' },
  { minutos: 1440, rotulo: '1 dia' },
];

export const cursoAtivo = (curso) => curso?.status === 'ativo';

// Valores efetivos do curso (os mesmos que o bot usa quando o campo falta ou é 0)
export function configDoCurso(curso = {}) {
  const lembrete = curso.lembreteAposHoras;
  return {
    intervalo: Number(curso.intervaloEtapasEmMinutos) || PADRAO_INTERVALO,
    aprovacao: Number(curso.percentualAprovacao) || PADRAO_APROVACAO,
    lembrete: lembrete === undefined || lembrete === null || lembrete === '' || Number.isNaN(Number(lembrete))
      ? PADRAO_LEMBRETE
      : Number(lembrete),
  };
}

export const semAcento = (texto) => String(texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '');
export const paraBusca = (texto) => semAcento(texto).toLowerCase();

// ---------- comando ----------
const PALAVRAS_VAZIAS = new Set(['A', 'O', 'AS', 'OS', 'DA', 'DE', 'DO', 'DAS', 'DOS', 'E', 'EM', 'NA', 'NO', 'NAS', 'NOS',
  'UM', 'UMA', 'PARA', 'POR', 'COM', 'QUE', 'AO', 'AOS', 'SEU', 'SUA']);

// "Segurança da Informação" -> "INICIAR_SEGURANCA_INFORMACAO" (até 3 palavras)
export function gerarComando(nome) {
  const palavras = semAcento(nome).toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  const uteis = palavras.filter((p) => !PALAVRAS_VAZIAS.has(p));
  const escolhidas = (uteis.length ? uteis : palavras).slice(0, 3);
  return `INICIAR_${escolhidas.join('_')}`.slice(0, LIMITES.comando);
}

// Normaliza o que a pessoa digita: maiúsculas, sem acento, espaço vira _
export function normalizarComando(texto) {
  return semAcento(texto).toUpperCase().replace(/[\s-]+/g, '_').replace(/[^A-Z0-9_]/g, '').slice(0, LIMITES.comando);
}

const mesmoComando = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();

export function cursoComComando(cursos, comando, ignorarId) {
  return cursos.find((c) => c.id !== ignorarId && mesmoComando(c.comando, comando)) || null;
}

// Acrescenta _2, _3... se o comando já existir no tenant
export function comandoDisponivel(base, cursos, ignorarId) {
  if (!cursoComComando(cursos, base, ignorarId)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidato = `${base.slice(0, LIMITES.comando - 3)}_${n}`;
    if (!cursoComComando(cursos, candidato, ignorarId)) return candidato;
  }
  return base;
}

export function validarComando(comando, cursos, ignorarId) {
  if (!comando) return 'Informe o comando.';
  if (!comando.startsWith('INICIAR_')) return 'O comando precisa começar com INICIAR_.';
  if (!/^INICIAR_[A-Z0-9_]*[A-Z0-9][A-Z0-9_]*$/.test(comando)) {
    return 'Depois de INICIAR_, use letras (A–Z), números ou _, sem espaços nem acentos.';
  }
  const outro = cursoComComando(cursos, comando, ignorarId);
  if (outro) return `Já existe um curso com este comando: ${outro.nome}.`;
  return '';
}

// ---------- números ----------
export function validarInteiro(valor, { min, max }, rotulo) {
  if (valor === '' || valor === null || valor === undefined) return `Informe ${rotulo}.`;
  const n = Number(valor);
  if (!Number.isInteger(n)) return 'Use um número inteiro.';
  if (n < min || n > max) return `Use um valor entre ${min} e ${max.toLocaleString('pt-BR')}.`;
  return '';
}

// 1 -> "1 min"; 60 -> "1 hora"; 90 -> "1 h 30 min"; 1440 -> "1 dia"; 1500 -> "1 dia 1 h"
export function formatarIntervalo(minutos) {
  const m = Math.max(0, Math.round(Number(minutos) || 0));
  if (m < 60) return `${m} min`;
  const dias = Math.floor(m / 1440);
  const horas = Math.floor((m % 1440) / 60);
  const mins = m % 60;
  if (!dias && !mins) return `${horas} ${horas === 1 ? 'hora' : 'horas'}`;
  const partes = [];
  if (dias) partes.push(`${dias} ${dias === 1 ? 'dia' : 'dias'}`);
  if (horas) partes.push(`${horas} h`);
  if (mins) partes.push(`${mins} min`);
  return partes.join(' ');
}

export const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

// ---------- conteúdo das etapas ----------
export const TIPOS_QUESTAO = {
  quiz: 'Quiz',
  pesquisa_multipla_escolha: 'Pesquisa · múltipla escolha',
  pesquisa_dissertativa: 'Pesquisa · dissertativa',
};

// "Pílula", "Quiz · Fácil", "Pesquisa · dissertativa"
export function rotuloEtapa(etapa) {
  if (etapa.tipoConteudo === 'pilula') return 'Pílula';
  const q = etapa.conteudo;
  if (!q) return 'Questão';
  if (q.type === 'quiz') return q.difficulty ? `Quiz · ${q.difficulty}` : 'Quiz';
  return TIPOS_QUESTAO[q.type] || 'Questão';
}

export function tituloEtapa(etapa) {
  const c = etapa.conteudo;
  if (c === undefined) return 'Carregando…';
  if (!c) return etapa.tipoConteudo === 'pilula' ? 'Pílula apagada' : 'Questão apagada';
  return (etapa.tipoConteudo === 'pilula' ? c.titulo : c.enunciado) || '(sem título)';
}

export const ehQuiz = (etapa) => etapa.tipoConteudo !== 'pilula' && etapa.conteudo?.type === 'quiz';

// Mesma lógica do bot: só alternativas com texto viram letras (pela posição original)
export function letrasValidas(opcoes) {
  return (Array.isArray(opcoes) ? opcoes : [])
    .map((o, i) => (o && o.text && String(o.text).trim() !== '' ? String.fromCharCode(65 + i) : null))
    .filter(Boolean);
}

// findCorrectAnswer do bot: a primeira alternativa marcada como correta
export function respostaCorreta(opcoes) {
  const lista = Array.isArray(opcoes) ? opcoes : [];
  const indice = lista.findIndex((o) => o && o.isCorrect);
  if (indice < 0) return { letra: null, justificativa: '' };
  return { letra: String.fromCharCode(65 + indice), justificativa: lista[indice].justificativa || '', indice };
}

// Problemas de uma questão de quiz que travam o aluno ou zeram a resposta
export function problemasDoQuiz(questao) {
  const letras = letrasValidas(questao.options);
  if (!letras.length) return { nivel: 'perigo', curto: 'Sem alternativas', texto: 'questão de quiz sem alternativas: o aluno não tem como responder e fica parado nesta etapa.' };
  const { letra } = respostaCorreta(questao.options);
  if (!letra) return { nivel: 'perigo', curto: 'Sem alternativa correta', texto: 'questão de quiz sem alternativa correta marcada: toda resposta conta como erro.' };
  if (!letras.includes(letra)) return { nivel: 'perigo', curto: 'Correta sem texto', texto: `a alternativa marcada como correta (${letra}) está sem texto: ninguém consegue acertar.` };
  const marcadas = (questao.options || []).filter((o) => o && o.isCorrect).length;
  if (marcadas > 1) return { nivel: 'atencao', curto: 'Mais de uma correta', texto: `mais de uma alternativa marcada como correta: só a primeira (${letra}) vale.` };
  return null;
}

// Avisos de qualidade do curso, do mais grave ao mais leve
export function avisosDoCurso(etapas, curso) {
  const avisos = [];
  const ativo = cursoAtivo(curso);
  if (!etapas.length) {
    avisos.push({ id: 'vazio', nivel: ativo ? 'perigo' : 'atencao', curto: 'sem etapas', texto: 'O curso ainda não tem etapas: quem entrar não recebe nada.' });
    return avisos;
  }
  etapas.forEach((etapa, i) => {
    const n = i + 1;
    if (etapa.conteudo === undefined) return; // ainda conferindo no banco
    if (!etapa.conteudo) {
      avisos.push({
        id: `apagado-${etapa.id}`, etapaId: etapa.id, nivel: 'perigo', curto: `conteúdo apagado na etapa ${n}`,
        texto: `Etapa ${n}: ${etapa.tipoConteudo === 'pilula' ? 'a pílula' : 'a questão'} foi apagada. O bot não consegue enviar e o aluno fica parado aqui. Remova a etapa.`,
      });
      return;
    }
    if (ehQuiz(etapa)) {
      const p = problemasDoQuiz(etapa.conteudo);
      if (p) avisos.push({ id: `quiz-${etapa.id}`, etapaId: etapa.id, nivel: p.nivel, curto: `etapa ${n}: ${p.curto.toLowerCase()}`, texto: `Etapa ${n}: ${p.texto}` });
    }
  });
  const existentes = etapas.filter((e) => e.conteudo);
  if (existentes.length && !existentes.some(ehQuiz)) {
    avisos.push({ id: 'sem-quiz', nivel: 'atencao', curto: 'sem questão de quiz', texto: 'Nenhuma questão de quiz: o curso não tem nota. A conclusão mostra nota 0.00 e todo mundo sai aprovado.' });
  }
  if (etapas[etapas.length - 1].tipoConteudo === 'pilula') {
    avisos.push({
      id: 'fim-pilula', nivel: 'atencao', curto: 'termina com pílula',
      texto: 'O curso termina com uma pílula: o bot só manda a conclusão com a nota depois da resposta à última etapa. Quem terminar não recebe a nota. Termine com uma questão.',
    });
  }
  const { intervalo } = configDoCurso(curso);
  if (ativo && intervalo < 60) {
    avisos.push({ id: 'intervalo', nivel: 'info', curto: 'intervalo de teste', texto: `Intervalo de ${formatarIntervalo(intervalo)} entre etapas: bom para testar, rápido demais para alunos de verdade.` });
  }
  const peso = { perigo: 0, atencao: 1, info: 2 };
  return avisos.sort((a, b) => peso[a.nivel] - peso[b.nivel]);
}

// ---------- alunos em andamento ----------
// Quantas etapas o bot já considera feitas para este aluno. ultimaEtapaConcluida é o
// índice da próxima etapa; em Aguardando_Resposta, a questão desse índice já foi enviada.
export function etapasJaEnviadas(progresso) {
  const k = Number(progresso.ultimaEtapaConcluida) || 0;
  return progresso.statusEtapa === 'Aguardando_Resposta' ? k + 1 : k;
}

// Alunos que pulariam ou repetiriam conteúdo se a sequência mudasse de `antes` para `depois`
// (listas de ids de etapa): o conjunto das etapas já enviadas deixa de ser o mesmo.
export function alunosAfetados(progressos, antes, depois) {
  return progressos.filter((p) => {
    const feitas = etapasJaEnviadas(p);
    if (!feitas) return false;
    const a = antes.slice(0, feitas);
    const b = depois.slice(0, feitas);
    if (a.length !== b.length) return true;
    const conjunto = new Set(a);
    return b.some((id) => !conjunto.has(id));
  });
}

// Índice da etapa em que cada aluno está (respondendo ou a próxima a receber)
export function alunosPorPosicao(progressos) {
  const mapa = {};
  progressos.forEach((p) => {
    const k = Number(p.ultimaEtapaConcluida) || 0;
    mapa[k] = (mapa[k] || 0) + 1;
  });
  return mapa;
}

// O tema do curso é gravado como id (cursos antigos guardavam o nome)
export function temaDoCurso(curso, temas) {
  const valor = String(curso?.tema || '').trim();
  if (!valor) return null;
  return temas.find((t) => t.id === valor)
    || temas.find((t) => String(t.nome || '').trim().toLowerCase() === valor.toLowerCase())
    || null;
}
